import { PropsWithChildren, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useSession } from '../contexts/AuthContext';
import { UserClaims } from '../services/authCacheManager.service';

type PrivateRouteProps = {
  loadingComponent: React.ReactNode;
  location?: Location;
  allowedRoles?: UserClaims['userRole'][];
};

const PrivateRoute: React.FC<PropsWithChildren<PrivateRouteProps>> = ({
  children,
  location,
  loadingComponent,
  allowedRoles,
}) => {
  const {
    isAuthenticated,
    isAuthenticating,
    refreshSession,
    buildSignInUrl,
    claims,
  } = useSession();
  const [isRoleAllowed, setIsRoleAllowed] = useState<boolean>(false);
  const routerLocation = useLocation();

  const loadingState =
    !isAuthenticated && isAuthenticating ? 'loading' : 'loaded';
  const authState =
    isAuthenticated && !isAuthenticating ? 'authenticated' : 'unauthenticated';

  const pathname = routerLocation.pathname;

  useEffect(() => {
    if (pathname) {
      refreshSession();
    }
  }, [pathname, refreshSession]);

  useEffect(() => {
    if (
      allowedRoles?.length &&
      claims?.userRole &&
      !['AIRSHIP_ADMIN', ...allowedRoles].includes(claims.userRole)
    ) {
      (location || window.location).replace('403');
    } else {
      setIsRoleAllowed(authState === 'authenticated');
    }
  }, [allowedRoles, claims?.userRole, authState, location]);

  useEffect(() => {
    if (loadingState === 'loaded' && authState === 'unauthenticated') {
      (location || window.location).replace(
        buildSignInUrl(window.location.href)
      );
    }
  }, [buildSignInUrl, location, loadingState, authState]);

  return loadingState === 'loading'
    ? loadingComponent
    : isRoleAllowed
      ? children
      : null;
};

export default PrivateRoute;
